import { action, computed, makeAutoObservable, observable } from 'mobx'

import { IFacet, ISitePermitFacets } from '~/client/graph'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import { isLogisticWorkflow } from '~/client/src/shared/utils/logisticsHelper'

export interface IFacetCount {
  viewableCount: number
  totalCount: number
}
type IFacetsMap = Record<LogisticsFilterType, Record<string, IFacetCount>>

class FacetAccumulator {
  public constructor(type: string) {
    this.type = type
  }

  private type: string
  private map: Record<string, IFacetCount> = {}

  private ensure(key: string) {
    if (!this.map[key]) this.map[key] = { viewableCount: 0, totalCount: 0 }
  }

  public addTotal(key: string, count: number = 1) {
    this.ensure(key)
    this.map[key].totalCount += count
  }

  public addViewable(key: string, count: number = 1) {
    this.ensure(key)
    this.map[key].viewableCount += count
  }

  public result() {
    return this.map
  }
}

type FacetConfig = {
  type: LogisticsFilterType
  selector?: (item: ILogisticItem) => string | string[]
  predicate?: (item: ILogisticItem) => boolean
  totalKey?: keyof ISitePermitFacets
  viewKey?: keyof ISitePermitFacets
}

export default class LogisticsListFacetStore {
  @observable private viewFacets: ISitePermitFacets = null
  @observable private totalFacets: ISitePermitFacets = null

  public constructor(
    private getFiltered: () => ILogisticItem[],
    private getAll: () => ILogisticItem[],
    private getFilteredExcluding: (
      excludedFilters: string[],
    ) => ILogisticItem[],
    private isFilterTypeActive: (filterType: LogisticsFilterType) => boolean,
    private currentProjectId?: string,
  ) {
    makeAutoObservable(this, {
      facets: computed({ keepAlive: true }),
    })
  }

  @action.bound
  public setViewableWorkflowFacets(f: ISitePermitFacets) {
    this.viewFacets = f
  }

  @action.bound
  public setTotalWorkflowFacets(f: ISitePermitFacets) {
    this.totalFacets = f
  }

  @computed
  private get allItems() {
    return this.getAll()
  }

  @computed
  private get filteredItems() {
    return this.getFiltered()
  }

  @computed
  public get facets(): IFacetsMap {
    const configs: FacetConfig[] = [
      {
        type: LogisticsFilterType.Status,
        viewKey: 'statusFilters',
        totalKey: 'statusFilters',
      },
      {
        type: LogisticsFilterType.Type,
        selector: i => i.app,
        viewKey: 'typeFilters',
        totalKey: 'typeFilters',
      },
      {
        type: LogisticsFilterType.Category,
        viewKey: 'categoryFilters',
        totalKey: 'categoryFilters',
      },
      {
        type: LogisticsFilterType.Company,
        viewKey: 'companyFilters',
        totalKey: 'companyFilters',
      },
      {
        type: LogisticsFilterType.Location,
        viewKey: 'locationFilters',
        totalKey: 'locationFilters',
      },
      {
        type: LogisticsFilterType.Equipment,
        viewKey: 'equipmentFilters',
        totalKey: 'equipmentFilters',
      },
      {
        type: LogisticsFilterType.ResponsibleContact,
        viewKey: 'assigneeFilters',
        totalKey: 'assigneeFilters',
      },
      {
        type: LogisticsFilterType.Attachment,
        viewKey: 'fileFilters',
        totalKey: 'fileFilters',
      },
      {
        type: LogisticsFilterType.Project,
        predicate: i => i.isShared,
        selector: i => {
          if (isLogisticWorkflow(i)) {
            return i.projectId
          }
        },
      },
    ]

    const accMap = configs.reduce(
      (acc, cfg) => {
        acc[cfg.type] = new FacetAccumulator(cfg.type)
        return acc
      },
      {} as Record<LogisticsFilterType, FacetAccumulator>,
    )

    const norm = (v: string | string[]) =>
      v == null
        ? [UNASSIGNED_FILTER_OPTION]
        : Array.isArray(v)
          ? v.length
            ? v
            : [UNASSIGNED_FILTER_OPTION]
          : [v]

    // tally totals and viewables in-memory
    for (const item of this.allItems) {
      for (const cfg of configs) {
        if (cfg.selector && (!cfg.predicate || cfg.predicate(item))) {
          norm(cfg.selector(item)).forEach(key =>
            accMap[cfg.type].addTotal(key),
          )
        }
      }
    }

    // For viewable counts: use filteredItems for inactive filters, excludedItems for active filters
    for (const item of this.filteredItems) {
      for (const cfg of configs) {
        if (
          !this.isFilterTypeActive(cfg.type) &&
          cfg.selector &&
          (!cfg.predicate || cfg.predicate(item))
        ) {
          norm(cfg.selector(item)).forEach(key =>
            accMap[cfg.type].addViewable(key),
          )
        }
      }
    }

    // For filter types with active selections, use excluded collection
    for (const cfg of configs) {
      if (this.isFilterTypeActive(cfg.type)) {
        const excludedItems = this.getFilteredExcluding([cfg.type])
        for (const item of excludedItems) {
          if (cfg.selector && (!cfg.predicate || cfg.predicate(item))) {
            norm(cfg.selector(item)).forEach(key =>
              accMap[cfg.type].addViewable(key),
            )
          }
        }
      }
    }

    for (const cfg of configs) {
      const acc = accMap[cfg.type]
      if (cfg.totalKey && this.totalFacets) {
        const totalArr = this.totalFacets[cfg.totalKey] as IFacet[]
        totalArr.forEach(({ key, count }) => acc.addTotal(key, count))
      }
      if (cfg.viewKey && this.viewFacets) {
        const viewArr = this.viewFacets[cfg.viewKey] as IFacet[]
        viewArr.forEach(({ key, count }) => acc.addViewable(key, count))
      }
    }

    const totalStatusSum =
      this.totalFacets?.statusFilters.reduce((sum, f) => sum + f.count, 0) || 0
    const viewableStatusSum =
      this.viewFacets?.statusFilters.reduce((sum, f) => sum + f.count, 0) || 0

    if (this.currentProjectId) {
      accMap[LogisticsFilterType.Project].addTotal(
        this.currentProjectId,
        totalStatusSum,
      )
      accMap[LogisticsFilterType.Project].addViewable(
        this.currentProjectId,
        viewableStatusSum,
      )
    }

    const out = Object.values(LogisticsFilterType).reduce(
      (out, type) => ({
        ...out,
        [type]: accMap[type]?.result() || {},
      }),
      {} as IFacetsMap,
    )

    return out
  }
}
